import { useEffect, useState } from 'react';
import { useCachedImageSrc } from '../hooks/useCachedImageSrc';

/**
 * Event card / hero image with graceful fallback - never the broken-image glyph. Served from the image store once
 * seen, so it is still there with no service (lib/images/imageCache.js); a failed load retries when service returns.
 */
export default function EventCardImage({
  src,
  alt = '',
  width,
  height,
  className = '',
  loading = 'lazy',
  placeholderClassName = '',
}) {
  const { src: resolved, onLoad, retryKey } = useCachedImageSrc(src);
  const [failed, setFailed] = useState(false);

  // Reset on the RESOLVED src: with signal but no throughput the plain URL fails first and the stored copy swaps in a
  // moment later - keyed on the prop, the failure hid that copy (2026-10-04 audit).
  useEffect(() => {
    setFailed(false);
  }, [resolved, retryKey]);

  if (!resolved || failed) {
    return (
      <div
        aria-hidden={!alt}
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
        style={{ width, height }}
        className={`flex items-center justify-center bg-gradient-to-br from-accent-800/90 to-accent-950/90 text-lg text-ink/70 ${placeholderClassName || className}`}
      >
        🎵
      </div>
    );
  }

  return (
    <img
      key={retryKey}
      src={resolved}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      onLoad={onLoad}
      onError={() => setFailed(true)}
      className={className}
    />
  );
}
